import * as logger from 'loglevel';
import dayjs from 'dayjs';
import axiosWrapper from '../../helpers/axiosWrapper';
import { NO_DURATION_DEFINED } from '../../helpers/axiosWrapper/cache';
import transformRequestHistogram from './transformRequest/transformRatesHistogram';
import transformRatesAggregation from './transformRequest/transformRatesAggregation';
import differenceInDays from 'date-fns/differenceInDays';
import startOfToday from 'date-fns/startOfToday';

/**
 * Here we find the lowest fare
 */
const lowestFare = (data, blackoutDates = []) => {
  const lowest = Math.min(
    ...data
      .filter((item) => {
        if (blackoutDates.length !== 0) {
          return (
            item.rates.length > 0 &&
            item.rates[0].priceSpecification.averageDailyPrice &&
            !isBlackoutDate(dayjs(item.date).toDate(), blackoutDates)
          );
        }
        return item.rates.length > 0 && item.rates[0].priceSpecification.averageDailyPrice;
      })
      .map((value) => {
        return value.rates[0].priceSpecification.averageDailyPrice;
      })
  );

  const lowerFormatted = data.find((item) => {
    const rate = item.rates[0];
    return (
      rate &&
      rate.priceSpecification.averageDailyPrice &&
      rate.priceSpecification.averageDailyPrice === lowest
    );
  });
  return lowerFormatted?.rates[0]?.priceSpecification.averageDailyPrice;
};

const isBlackoutDate = (date, blackoutDates) => {
  const newDate = dayjs(date);
  for (let index = 0; index < blackoutDates.length; index++) {
    const element = blackoutDates[index];
    if (
      dayjs(element.start).isSame(newDate) ||
      dayjs(element.end).isSame(newDate) ||
      (dayjs(newDate).isAfter(element.start) && dayjs(newDate).isBefore(element.end))
    ) {
      return true;
    }
  }
  return false;
};

/**
 * The group routes API returns the data in non linear format
 * so this function will intend normalize the data for the components
 * @param {*} faresRawData
 * @param {*} query
 */
const transformData = (histogramRawData, filter) => {
  const {
    blackoutDates,
    showShortPriceGreaterThan = 4,
    cutRatePrice = false,
    rateTags = []
  } = filter;
  const isWeekend = rateTags.length === 1 && rateTags.find((element) => element === 'WEEKEND');
  const lowestRate = lowestFare(histogramRawData.histogram, blackoutDates);
  const histogram = histogramRawData.histogram.map((item) => {
    let rateItem = item.rates && Array.isArray(item.rates) ? item.rates[0] : null;
    if (isWeekend && dayjs(item.date).day() < 5) {
      rateItem = { dataType: 'EMPTY', departureDate: item.date };
    } else if (rateItem) {
      let { searchDate } = rateItem;
      // For date in safary y ie
      if (searchDate && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+[+|-]\d{4}/.test(searchDate)) {
        searchDate = `${searchDate.substring(0, searchDate.length - 2)}:${searchDate.substring(
          searchDate.length - 2
        )}`;
      }
      rateItem.dataType = 'DATA_API';
      //PENDING: Decide through a prePopulation settings the condition to show the shortenedFormattedTotalPrice or the formattedTotalPrice
      rateItem = {
        ...rateItem,
        ...rateItem.priceSpecification,
        locationRequest: histogramRawData.location
      };
      delete rateItem.priceSpecification;
      rateItem.departureDate = rateItem.formattedCheckinDate;
      rateItem.formattedDepartureDate = rateItem.formattedCheckinDate;
      rateItem.returnDate = rateItem.formattedCheckoutDate;
      rateItem.formattedReturnDate = rateItem.formattedCheckoutDate;
      rateItem.flightDeltaDays = rateItem.nightCount;

      let priceLength = parseInt(rateItem.averageDailyPrice).toString().length;
      if (priceLength > showShortPriceGreaterThan) {
        rateItem.showShortPrice = true;
      }

      if (cutRatePrice) {
        rateItem.shortenedFormattedAverageDailyPrice = rateItem.shortenedFormattedAverageDailyPrice.replace(
          rateItem.currencyCode,
          ''
        );
      }
    } else {
      rateItem = { dataType: 'EMPTY_FROM_API', departureDate: item.date };
    }
    if (blackoutDates) {
      rateItem.isBlackoutDay = isBlackoutDate(item.date, blackoutDates);
    }
    return {
      day: dayjs(item.date).toDate(),
      fareItem: rateItem,
      isLowest: rateItem.dataType === 'DATA_API' ? rateItem.averageDailyPrice === lowestRate : false
    };
  });
  return histogram;
};

/**
 * The rates services API returns the data in non linear format
 * so this function will intend normalize the data for the components
 * @param {*} ratesRawData
 * @param {Object} filter
 */
const transformRatesDataAggregation = (ratesRawData, filter) => {
  const response = [];
  const { aggregationType } = filter;
  const raw = ratesRawData.pop();
  // let oneColumn = false;

  raw.results?.forEach((firstLevel) => {
    const resultFirstLevel = {
      type: 'firstLevel',
      value: firstLevel.firstLevel,
      data: [],
      images: []
    };
    // oneColumn = raw.results.length === 1 && aggregationType === 'PROPERTY_ROOM';
    firstLevel.results.forEach((secondLevel) => {
      while (secondLevel.results.length !== 0) {
        const rawItemRaw = secondLevel.results.pop();
        const rateItem = {
          ...rawItemRaw,
          ...rawItemRaw.priceSpecification
        };
        const {
          location: {
            hierarchy: {
              property: {
                mainImage: { url, alt }
              }
            }
          }
        } = rateItem;
        rateItem.hotelName = rateItem.location.hierarchy.property.name;
        rateItem.countryName = 'Country';
        rateItem.departureDate = rateItem.formattedCheckinDate;
        rateItem.formattedDepartureDate = rateItem.formattedCheckinDate;
        rateItem.returnDate = rateItem.formattedCheckoutDate;
        rateItem.formattedReturnDate = rateItem.formattedCheckoutDate;
        rateItem.flightDeltaDays = rateItem.nightCount;
        rateItem.formattedTotalPrice = rateItem.formattedAverageDailyPrice;

        if (aggregationType === 'PROPERTY_ROOM') {
          let roomImage,
            roomAltImage,
            roomName = '';
          if (
            rateItem?.location?.metadata?.room?.mainImage &&
            rateItem?.location?.metadata?.room?.name
          ) {
            roomImage = rateItem?.location?.metadata?.room?.mainImage?.url;
            roomAltImage = rateItem?.location?.metadata?.room?.mainImage?.alt;
            roomName = rateItem?.location?.metadata?.room?.name;
          }
          rateItem.roomName = roomName;
          rateItem.flavor = 'PROPERTY_MODE';
          resultFirstLevel.images.push({ url: roomImage, alt: roomAltImage });
        }

        if (aggregationType === 'CITY_PROPERTY') {
          rateItem.flavor = 'CITY_PROPERTY';
        }

        delete rateItem.priceSpecification;
        resultFirstLevel.data.push(rateItem);
        resultFirstLevel.images.push({ url, alt });
      }
    });

    resultFirstLevel.image = [
      ...new Map(resultFirstLevel.images.map((image) => [image.url, image])).values()
    ];
    response.push(resultFirstLevel);
  });
  return response;
};

/**
 * Makes sure the rates response is complain with a basic schema
 * @param {Array of objects} rates
 * @returns
 */
const validateRatesResponse = (rates) => {
  const validation = { isValid: true, message: '' };
  if (!rates || !rates[0].results || !Array.isArray(rates[0].results)) {
    validation.isValid = false;
    return {
      isValid: false,
      data: rates,
      message: 'INTEGRATION__RATES: Result rates must be an Array'
    };
  }
  if (rates[0].results.length > 0) {
    const rate = rates[0].results[0];
    if (
      !rate.results[0].results[0].propertyCode ||
      !rate.results[0].results[0].priceSpecification
    ) {
      return {
        isValid: false,
        data: rate,
        message:
          'INTEGRATION__RATES: RATES SCHEMA is invalid, please check RATES API documentation.'
      };
    }
  }
  return validation;
};
/**
 * Makes sure the histogram response is complain with a basic schema
 * @param {*} histogram
 */
const validateHistogramResponse = (histograms) => {
  const validation = { isValid: true, message: '' };
  if (!histograms || !histograms.histogram || !Array.isArray(histograms.histogram)) {
    validation.isValid = false;
    return {
      isValid: false,
      data: histograms,
      message: 'INTEGRATION__HISTOGRAM: Histograms must be an Array'
    };
  }
  if (histograms.length > 0) {
    const histogram = histograms[0];
    if (!histogram.origin || !histogram.destination) {
      return {
        isValid: false,
        data: histogram,
        message:
          'INTEGRATION__HISTOGRAM: Histogram SCHEMA is invalid, please check Histograms API documentation.'
      };
    }
  }
  return validation;
};

/**
 * Retrieve fares from the GroupRoutes Endpoint
 * @param {*} filter
 * @param {*} integrationSettings
 */
const getHistograms = (filter, integrationSettings, meta, pageContext) => {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__HISTOGRAM: **integrationSettings** not especified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const { v1HospitalityRateHistogram: histogramApiSettings } = integrationSettings;
    const { cache = false, cacheDuration = NO_DURATION_DEFINED } = histogramApiSettings;
    if (
      !histogramApiSettings ||
      !histogramApiSettings.method ||
      !histogramApiSettings.endPoint ||
      !histogramApiSettings.headers
    ) {
      const MESSAGE = `INTEGRATION__HISTOGRAM: **integrationSettings.histogramApiSettings** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const filterCopy = { ...filter };

    filterCopy.checkinInterval = {
      start: differenceInDays(filter.dateRange.start, startOfToday()),
      end: differenceInDays(filter.dateRange.end, startOfToday())
    };

    axiosWrapper(
      {
        method: histogramApiSettings.method,
        url: `${histogramApiSettings.endPoint}`,
        headers: { ...histogramApiSettings.headers },
        data: transformRequestHistogram(filterCopy)
      },
      {
        resource: 'INTEGRATION__HISTOGRAM',
        meta,
        integrationSettings,
        pageContext,
        cache,
        cacheDuration
      }
    )
      .then((response) => {
        const validation = validateHistogramResponse(response.data);
        if (validation.isValid) {
          logger.info('INTEGRATION__HISTOGRAM: Successful request', response.data);
          const histograms = transformData(response.data, filterCopy);
          resolve(histograms);
        } else {
          logger.error(validation.message, validation.data);
          reject(response.data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const getRatesWithAggregation = (filter, integrationSettings, meta, pageContext) => {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__RATES: **integrationSettings** not specified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const { v1HospitalityRatesAggregation: ratesApiSettings } = integrationSettings;
    const { cache = false, cacheDuration = NO_DURATION_DEFINED } = ratesApiSettings;
    if (
      !ratesApiSettings ||
      !ratesApiSettings.method ||
      !ratesApiSettings.endPoint ||
      !ratesApiSettings.headers
    ) {
      const MESSAGE = `INTEGRATION__RATES: **integrationSettings.v1HospitalityRatesAggregation** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const filterCopy = { ...filter };

    if (filterCopy.dateRange?.start && filterCopy.dateRange?.end) {
      filterCopy.checkinInterval = {
        start: differenceInDays(filter.dateRange.start, startOfToday()),
        end: differenceInDays(filter.dateRange.end, startOfToday())
      };
    }

    if (filterCopy?.propertyCode) {
      filterCopy.hierarchy = {};
      if (filterCopy?.layout === 'home') {
        filterCopy.hierarchy['country'] = {
          geoIds: [filterCopy?.propertyCode]
        };
      } else {
        filterCopy.hierarchy[filterCopy.layout] = {
          geoIds: [filterCopy?.propertyCode]
        };
      }
    }

    axiosWrapper(
      {
        method: ratesApiSettings.method,
        url: `${ratesApiSettings.endPoint}`,
        headers: { ...ratesApiSettings.headers },
        data: transformRatesAggregation(filterCopy)
      },
      {
        resource: 'INTEGRATION__RATES',
        meta,
        integrationSettings,
        pageContext,
        cache,
        cacheDuration
      }
    )
      .then((response) => {
        const validation = validateRatesResponse(response.data);
        if (validation.isValid) {
          logger.info('INTEGRATION__RATES: Successful request', response.data);
          const rates = transformRatesDataAggregation(response.data, filterCopy);
          resolve(rates);
        } else {
          logger.error(validation.message, validation.data);
          reject(response.data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const getRates = (filter, integrationSettings, faresTransformationLogic, meta, pageContext) => {
  if (faresTransformationLogic === 'aggregation') {
    return getRatesWithAggregation(filter, integrationSettings, meta, pageContext);
  }

  return new Promise((resolve, reject) => {
    resolve([]);
  });
};

export { getHistograms, getRates, transformData, lowestFare, validateHistogramResponse };
